import React from "react";
const goalLabels = {
  fat_loss: "Fat loss",
  performance: "Performance",
  recomp: "Recomposition",
};

const formatKcal = (value) =>
  Number.isFinite(value) ? Math.round(value) : null;

const formatSessionCount = (value) => {
  if (!Number.isFinite(value) || value <= 0) return null;
  const rounded = Math.round(value * 10) / 10;
  return Number.isInteger(rounded) ? `${rounded}` : rounded.toFixed(1);
};

export default function ResultsPanel({
  results,
  basicsValid,
  strengthCount,
  cardioCount,
  accuracyScore = 0,
  trainingSource = "manual",
  trainingSummary = "",
  layout = "default",
  compact = false,
}) {
  const {
    bmr,
    tdee,
    recommendedTarget,
    protein,
    leanMassLb,
    bodyFatPct,
    bmrMethod,
    baseActivity,
    assumedActivity,
    modifiers,
    totalMultiplier,
    rawMultiplier,
    wasClamped,
    goal,
    baselineTdee,
    delta,
    calculatorNetMultiplier,
    calculatorNetLabel,
    stepsCaloriesPerDay,
    stepsBaseline,
    strengthCaloriesPerWeek,
    cardioCaloriesPerWeek,
    workoutCaloriesPerWeek,
    workoutCaloriesPerDay,
    workoutSessionsPerWeek,
    strengthSessionsPerWeek,
    cardioSessionsPerWeek,
  } = results;

  const hasResults = basicsValid && Number.isFinite(bmr);
  const deltaValue = Number.isFinite(delta)
    ? Math.round(Math.abs(delta))
    : null;
  const deltaDirection =
    Number.isFinite(delta) && delta !== 0
      ? delta > 0
        ? "higher"
        : "lower"
      : "different";
  const resultsSubtitle =
    layout === "resultsOnly"
      ? "Snapshot based on your saved inputs."
      : "Live update as you add detail.";
  const sessionAverage =
    Number.isFinite(workoutCaloriesPerWeek) &&
    Number.isFinite(workoutSessionsPerWeek) &&
    workoutSessionsPerWeek > 0
      ? workoutCaloriesPerWeek / workoutSessionsPerWeek
      : null;
  const strengthSessionsLabel = formatSessionCount(
    strengthSessionsPerWeek
  );
  const cardioSessionsLabel = formatSessionCount(
    cardioSessionsPerWeek
  );
  const totalSessionsLabel = formatSessionCount(
    workoutSessionsPerWeek
  );
  let sessionSummary = "(add sessions to estimate)";
  if (totalSessionsLabel) {
    if (strengthSessionsLabel && cardioSessionsLabel) {
      sessionSummary = `(${totalSessionsLabel} total sessions/week: ${strengthSessionsLabel} strength + ${cardioSessionsLabel} cardio)`;
    } else if (strengthSessionsLabel) {
      sessionSummary = `(${strengthSessionsLabel} strength sessions/week)`;
    } else if (cardioSessionsLabel) {
      sessionSummary = `(${cardioSessionsLabel} cardio sessions/week)`;
    } else {
      sessionSummary = `(${totalSessionsLabel} sessions/week)`;
    }
  }
  const hasStrengthDetail = strengthCount > 0;
  const hasCardioDetail = cardioCount > 0;
  const hasBodyFat = Number.isFinite(bodyFatPct);
  const strengthSessionsCompact = strengthSessionsLabel
    ? `${strengthSessionsLabel}S`
    : null;
  const cardioSessionsCompact = cardioSessionsLabel
    ? `${cardioSessionsLabel}C`
    : null;
  const sessionBreakdownCompact = [
    strengthSessionsCompact,
    cardioSessionsCompact,
  ]
    .filter(Boolean)
    .join("/");
  const sessionSummaryCompact = totalSessionsLabel
    ? `${totalSessionsLabel}/wk${
        sessionBreakdownCompact ? ` ${sessionBreakdownCompact}` : ""
      }`
    : null;
  const sessionLabelCompact = sessionSummaryCompact
    ? `(${sessionSummaryCompact})`
    : "(add sessions)";

  const accuracySection = (
    <div className="glass-card p-5">
      <div className="flex items-center justify-between text-xs uppercase tracking-[0.3em] text-[var(--muted)]">
        <span>Accuracy Estimation</span>
        <span className="font-semibold text-[var(--ink)]">
          {Math.round(accuracyScore)}%
        </span>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-white/60">
        <div
          className="h-2 rounded-full"
          style={{
            width: `${Math.min(95, accuracyScore)}%`,
            background:
              "linear-gradient(90deg, var(--accent-2), var(--accent))",
            transition: "width 600ms ease",
          }}
        />
      </div>
      <p className="mt-2 text-xs text-[var(--muted)]">
        More detail increases confidence.
      </p>
    </div>
  );

  const resultsSection = (
    <div className="glass-card p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-display text-[var(--ink)]">
              Results
            </h3>
            <p className="text-sm text-[var(--muted)]">
              {resultsSubtitle}
            </p>
          </div>
          {goal ? (
            <span className="rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
              {goalLabels[goal]}
            </span>
          ) : null}
        </div>

        <div className="mt-5 grid gap-4">
          <div className="grid gap-3 rounded-2xl border border-white/70 bg-white/70 p-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--muted)]">BMR</span>
              <span className="text-lg font-semibold text-[var(--ink)]">
                {hasResults ? `${formatKcal(bmr)} kcal` : "--"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--muted)]">TDEE</span>
              <span className="text-lg font-semibold text-[var(--ink)]">
                {hasResults ? `${formatKcal(tdee)} kcal` : "--"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--muted)]">
                Recommended target
              </span>
              <span className="text-lg font-semibold text-[var(--ink)]">
                {hasResults && Number.isFinite(recommendedTarget)
                  ? `${formatKcal(recommendedTarget)} kcal`
                  : "--"}
              </span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-[var(--muted)]">
                Protein (1.2 g/lb lean mass)
              </span>
              <span className="text-lg font-semibold text-[var(--ink)]">
                {hasResults && Number.isFinite(protein)
                  ? `${protein} g`
                  : "--"}
              </span>
            </div>
            <p className="text-xs text-[var(--muted)]">
              {hasBodyFat && Number.isFinite(leanMassLb)
                ? `Lean mass ${Math.round(
                    leanMassLb
                  )} lb from ${bodyFatPct}% body fat.`
                : "Add body fat % to refine BMR and protein target."}
            </p>
          </div>

          {Number.isFinite(stepsCaloriesPerDay) &&
          stepsCaloriesPerDay > 0 ? (
            <div className="rounded-2xl border border-white/70 bg-white/70 p-4 text-sm text-[var(--muted)]">
              <p>
                Daily steps add{" "}
                <span className="font-semibold text-[var(--ink)]">
                  {formatKcal(stepsCaloriesPerDay)} kcal/day
                </span>{" "}
                {Number.isFinite(stepsBaseline)
                  ? `(above ${Math.round(
                      stepsBaseline
                    )} baseline steps)`
                  : ""}
                .
              </p>
            </div>
          ) : null}
        </div>
    </div>
  );

  const comparisonSection = (
    <div className="glass-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-base font-display text-[var(--ink)]">
              Comparison check
            </h4>
            <p className="text-sm text-[var(--muted)]">
              Calculator.net&apos;s BMR calculator uses a single
              activity multiplier based on your selected activity
              level. That baseline can be inaccurate when training
              volume varies.
            </p>
          </div>
          <span className="rounded-full bg-white/70 px-3 py-1 text-xs uppercase tracking-[0.2em] text-[var(--muted)]">
            {calculatorNetLabel || "Activity level"}
          </span>
        </div>

        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div className="comparison-card rounded-2xl border border-white/70 bg-white/70 p-4">
            <p className="comparison-label text-xs text-[var(--muted)]">
              Calculator.net BMR calculator
            </p>
            <p className="mt-1 text-xs text-[var(--muted)]">
              Activity level{" "}
              <span className="font-semibold text-[var(--ink)]">
                {calculatorNetLabel || "Sedentary"}
              </span>{" "}
              {Number.isFinite(calculatorNetMultiplier)
                ? `(${calculatorNetMultiplier.toFixed(3)})`
                : ""}
            </p>
            <p className="mt-2 text-xl font-semibold text-[var(--ink)]">
              {hasResults && Number.isFinite(baselineTdee)
                ? `${formatKcal(baselineTdee)} kcal`
                : "--"}
            </p>
            <p className="mt-1 text-xs text-[var(--muted)]">
              Uses selected activity level (no workout detail).
            </p>
          </div>
          <div className="comparison-card rounded-2xl border border-white/70 bg-white/70 p-4">
            <p className="comparison-label text-xs text-[var(--muted)]">
              BetterBMR<wbr />Calculator
            </p>
            <p className="mt-2 text-xl font-semibold text-[var(--ink)]">
              {hasResults && Number.isFinite(tdee)
                ? `${formatKcal(tdee)} kcal`
                : "--"}
            </p>
            <p className="mt-1 text-xs text-[var(--muted)]">
              Uses training frequency + workout detail.
            </p>
          </div>
        </div>

        <p className="mt-4 text-sm text-[var(--muted)]">
          {hasResults && Number.isFinite(deltaValue)
            ? `Potential inaccuracy vs calculator.net baseline: ${deltaValue} kcal/day ${deltaDirection}.`
            : "Fill in the basics to see the difference in kcal/day."}
        </p>
    </div>
  );

  const comparisonSectionCompact = (
    <div className="glass-card p-6">
      <div className="flex items-center justify-between gap-3">
        <h4 className="text-base font-display text-[var(--ink)]">
          Comparison
        </h4>
        <span className="rounded-full bg-white/70 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
          {calculatorNetLabel || "Activity level"}
        </span>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2">
        <div className="comparison-card rounded-2xl border border-white/70 bg-white/70 p-3">
          <p className="comparison-label text-[10px] text-[var(--muted)]">
            Calculator.net
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(baselineTdee)
              ? `${formatKcal(baselineTdee)} kcal`
              : "--"}
          </p>
        </div>
        <div className="comparison-card rounded-2xl border border-white/70 bg-white/70 p-3">
          <p className="comparison-label text-[10px] text-[var(--muted)]">
            BetterBMR
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(tdee)
              ? `${formatKcal(tdee)} kcal`
              : "--"}
          </p>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-[var(--muted)]">
        {hasResults && Number.isFinite(deltaValue)
          ? `${deltaValue} kcal/day ${deltaDirection} vs baseline.`
          : "Fill basics to compare."}
      </p>
    </div>
  );

  const resultsCompact = (
    <div className="glass-card p-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-display text-[var(--ink)]">
            Results
          </h3>
          <p className="text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
            Accuracy Estimation
          </p>
        </div>
        <div className="text-right">
          {goal ? (
            <span className="rounded-full bg-white/70 px-2 py-1 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
              {goalLabels[goal]}
            </span>
          ) : null}
          <div className="mt-1 text-base font-semibold text-[var(--ink)]">
            {Math.round(accuracyScore)}%
          </div>
        </div>
      </div>
      <div className="mt-2 h-1.5 w-full rounded-full bg-white/60">
        <div
          className="h-1.5 rounded-full"
          style={{
            width: `${Math.min(95, accuracyScore)}%`,
            background:
              "linear-gradient(90deg, var(--accent-2), var(--accent))",
            transition: "width 600ms ease",
          }}
        />
      </div>
      <div className="mt-3 grid gap-2 text-xs">
        <div className="flex items-center justify-between">
          <span className="text-[var(--muted)]">BMR</span>
          <span className="font-semibold text-[var(--ink)]">
            {hasResults ? `${formatKcal(bmr)} kcal` : "--"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--muted)]">TDEE</span>
          <span className="font-semibold text-[var(--ink)]">
            {hasResults ? `${formatKcal(tdee)} kcal` : "--"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--muted)]">Target</span>
          <span className="font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(recommendedTarget)
              ? `${formatKcal(recommendedTarget)} kcal`
              : "--"}
          </span>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[var(--muted)]">Protein (1.2 g/lb LBM)</span>
          <span className="font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(protein)
              ? `${protein} g`
              : "--"}
          </span>
        </div>
      </div>
      <p className="mt-2 text-[10px] text-[var(--muted)]">
        {hasBodyFat && Number.isFinite(leanMassLb)
          ? `Lean mass ${Math.round(
              leanMassLb
            )} lb (${bodyFatPct}% body fat).`
          : "Add body fat % for lean-mass BMR + protein."}
      </p>
    </div>
  );

  const trainingCompact = (
    <div className="glass-card p-3 text-xs">
      <div className="flex items-center justify-between text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <span>Training detail</span>
        <span className="font-semibold text-[var(--ink)]">
          {trainingSource === "workout" ? "Workout-based" : "Manual"}
        </span>
      </div>
      <p className="mt-2 text-xs text-[var(--muted)]">
        {trainingSummary || "Select a training profile."}
      </p>
      <p className="mt-2 text-[10px] text-[var(--muted)]">
        {hasBodyFat && bmrMethod === "lean"
          ? "BMR adjusted using lean mass."
          : "BMR uses Mifflin-St Jeor unless body fat % is provided."}
      </p>
      {trainingSource === "workout" ? (
        <div className="mt-2 grid gap-1">
          <div className="flex items-center justify-between">
            <span className="text-[var(--muted)]">Workout kcal/week</span>
            <span className="font-semibold text-[var(--ink)]">
              {Number.isFinite(workoutCaloriesPerWeek)
                ? `${formatKcal(workoutCaloriesPerWeek)}`
                : "--"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--muted)]">Daily avg</span>
            <span className="font-semibold text-[var(--ink)]">
              {Number.isFinite(workoutCaloriesPerDay)
                ? `${formatKcal(workoutCaloriesPerDay)}`
                : "--"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--muted)]">
              Avg/session {sessionLabelCompact}
            </span>
            <span className="font-semibold text-[var(--ink)]">
              {Number.isFinite(sessionAverage)
                ? `${formatKcal(sessionAverage)}`
                : "--"}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-[var(--muted)]">Strength/Cardio</span>
            <span className="font-semibold text-[var(--ink)]">
              {Number.isFinite(strengthCaloriesPerWeek)
                ? `${formatKcal(strengthCaloriesPerWeek)}`
                : "--"}{" "}
              /{" "}
              {Number.isFinite(cardioCaloriesPerWeek)
                ? `${formatKcal(cardioCaloriesPerWeek)}`
                : "--"}
            </span>
          </div>
        </div>
      ) : null}
      {Number.isFinite(stepsCaloriesPerDay) &&
      stepsCaloriesPerDay > 0 ? (
        <p className="mt-2 text-[10px] text-[var(--muted)]">
          Steps add{" "}
          <span className="font-semibold text-[var(--ink)]">
            {formatKcal(stepsCaloriesPerDay)} kcal/day
          </span>
          {Number.isFinite(stepsBaseline)
            ? ` (above ${Math.round(stepsBaseline)} steps)`
            : ""}
          .
        </p>
      ) : null}
      <p className="mt-2 text-[10px] text-[var(--muted)]">
        {hasStrengthDetail || hasCardioDetail ? (
          <>
            Captured{" "}
            {hasStrengthDetail ? (
              <>
                <span className="font-semibold text-[var(--ink)]">
                  {strengthCount}
                </span>{" "}
                workout{strengthCount === 1 ? "" : "s"}
                {strengthSessionsLabel
                  ? ` (${strengthSessionsLabel}/wk)`
                  : ""}
              </>
            ) : null}
            {hasStrengthDetail && hasCardioDetail ? " and " : null}
            {hasCardioDetail ? (
              <>
                <span className="font-semibold text-[var(--ink)]">
                  {cardioCount}
                </span>{" "}
                cardio {cardioCount === 1 ? "entry" : "entries"}
                {cardioSessionsLabel
                  ? ` (${cardioSessionsLabel}/wk)`
                  : ""}
              </>
            ) : null}{" "}
            to refine activity load.
          </>
        ) : (
          <>Add training detail to lift confidence.</>
        )}
      </p>
    </div>
  );

  const comparisonCompact = (
    <div className="glass-card p-3 text-xs">
      <div className="flex items-center justify-between gap-3 text-[10px] uppercase tracking-[0.2em] text-[var(--muted)]">
        <span>Comparison</span>
        <span>{calculatorNetLabel || "Activity level"}</span>
      </div>
      <p className="mt-2 text-[11px] text-[var(--muted)]">
        Calculator.net uses a single activity multiplier baseline.
      </p>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div className="comparison-card rounded-xl border border-white/70 bg-white/70 p-3">
          <p className="comparison-label text-[10px] text-[var(--muted)]">
            Calculator.net
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(baselineTdee)
              ? `${formatKcal(baselineTdee)} kcal`
              : "--"}
          </p>
        </div>
        <div className="comparison-card rounded-xl border border-white/70 bg-white/70 p-3">
          <p className="comparison-label text-[10px] text-[var(--muted)]">
            BetterBMR
          </p>
          <p className="mt-1 text-sm font-semibold text-[var(--ink)]">
            {hasResults && Number.isFinite(tdee)
              ? `${formatKcal(tdee)} kcal`
              : "--"}
          </p>
        </div>
      </div>
      <p className="mt-2 text-[11px] text-[var(--muted)]">
        {hasResults && Number.isFinite(deltaValue)
          ? `Potential inaccuracy vs calculator.net baseline: ${deltaValue} kcal/day ${deltaDirection}.`
          : "Fill in the basics to see the difference in kcal/day."}
      </p>
    </div>
  );

  if (layout === "resultsOnly") {
    return (
      <div className="results-only-grid grid gap-2 md:grid-cols-2">
        <div className="order-1">{resultsCompact}</div>
        <div className="order-2">{trainingCompact}</div>
        <div className="order-3 md:col-span-2">
          {comparisonCompact}
        </div>
      </div>
    );
  }

  return (
    <div
      className={`space-y-6 ${
        compact ? "results-panel--compact" : ""
      }`}
    >
      {accuracySection}
      {resultsSection}
      {compact ? comparisonSectionCompact : comparisonSection}
    </div>
  );
}
